import { IpaType, ipaToFeatures, syllableBreak } from "../phonemes/ipa";
import { PhonemeInfo } from "./phonemeInfo";
import { Pattern } from "./pattern";

type Ignore = (item: string) => boolean;

export interface MatchResult {
  /** number of symbols matched, possibly 0 */
  readonly count: number;
  /** any stored subsets in the order they were found, segmented */
  readonly store?: IpaType[][];
}

/**
 * check if the phonemes starting at ipa[i] are a match for the given pattern;
 * if yes, return the count of matching phonemes (possibly 0), else return false;
 * note that any ignored items are part of the returned count
 */
export function isMatch(
  pattern: Pattern<PhonemeInfo>, ipa: IpaType[], i: number,
  ignore?: Ignore, lookup?: Record<string, IpaType[]>
): MatchResult | false {
  return localIsMatch(pattern, { ipa, i, ignore, lookup });
}

function localIsMatch(pattern: Pattern<PhonemeInfo>, info: MatchInfo): MatchResult | false {
  if ('seq' in pattern) {
    return checkSeq(pattern.seq, info);
  } else if ('and' in pattern) {
    return checkAnd(pattern.and, info);
  } else if ('or' in pattern) {
    return checkOr(pattern.or, info);
  } else if ('not' in pattern) {
    return checkNot(pattern.not, info);
  } else if ('opt' in pattern) {
    return checkOpt(pattern.opt, info);
  } else if ('store' in pattern) {
    return checkStore(pattern.store, info);
  } else {
    return isItemMatch(pattern, info);
  }
}

/** convenient bundle of parameters passed thru the various functions */
interface MatchInfo {
  readonly ipa: IpaType[];
  readonly i: number;
  readonly ignore?: Ignore;
  readonly lookup?: Record<string, IpaType[]>;
  readonly store?: IpaType[][]; // array of segmented IPA
}

function checkSeq(seq: Pattern<PhonemeInfo>[], info: MatchInfo): MatchResult | false {
  let j = 0;
  let loop = 0;
  let store = info.store;
  const { ipa, i } = info;
  for (const pattern of seq) {
    if (loop++ === 0 && 'special' in pattern && pattern.special === 'word' && checkSpecial(ipa, i - 1, 'word') === 0) {
      // start of pattern & the previous phoneme should be a word boundary
      continue;
    }
    const result = localIsMatch(pattern, { ...info, i: i + j, store });
    if (result === false) {
      return false; // everything must match
    } else if (result.count === 0 && !isOpt(pattern)) {
      return makeResult(j, info, result.store);  // e.g., we hit a word boundary so we're done
    }
    store = result.store;
    j += result.count;
  }
  return makeResult(j, info, store);
}
function isOpt(pattern: Pattern<PhonemeInfo>): boolean {
  return 'opt' in pattern || ('store' in pattern && 'opt' in pattern.store);
}

function checkAnd(and: Pattern<PhonemeInfo>[], info: MatchInfo): MatchResult | false {
  let max = 0;
  let store = info.store;
  for (const pattern of and) {
    const result = localIsMatch(pattern, info);
    if (result === false) {
      return false; // everything must match
    } else {
      if (result.count > max) {
        max = result.count;
      }
      store = result.store;
    }
  }
  return makeResult(max, info, store);
}

function checkOr(or: Pattern<PhonemeInfo>[], info: MatchInfo): MatchResult | false {
  for (const pattern of or) {
    const result = localIsMatch(pattern, info);
    if (result !== false) {
      return result;  // return first match
    }
  }
  return false;
}

function checkNot(not: Pattern<PhonemeInfo>, info: MatchInfo): MatchResult | false {
  const result = localIsMatch(not, info)
  return result !== false ? false : makeResult(1, info);
}

function checkOpt(opt: Pattern<PhonemeInfo>, info: MatchInfo): MatchResult | false {
  const result = localIsMatch(opt, info);
  // if it didn't match, treat that as matching 0 symbols
  return result === false ? makeResult(0, info) : result;
}

function checkStore(store: Pattern<PhonemeInfo>, info: MatchInfo): MatchResult | false {
  const result = localIsMatch(store, info);
  if (result === false) {
    return false;
  } else if (result.count === 0) {
    return result;
  }
  // store the result in the next available slot
  const value = info.ipa.slice(info.i, info.i + result.count);
  const newStore = result.store ? result.store.concat(value) : [value];
  return { count: result.count, store: newStore };
}

function isItemMatch(item: PhonemeInfo, info: MatchInfo): MatchResult | false {
  const { ipa, i, ignore } = info;
  if ('feature' in item) {
    for (let j = i; j < ipa.length; j++) {
      // are specified features part of ipa[j]'s features?
      const features = ipaToFeatures(ipa[j]);
      const isPresent = features && item.feature.every(f => features.includes(f)) ? makeResult(1 + j - i, info) : false;
      if (isPresent || !(ignore && ignore(ipa[j]))) {
        return isPresent;
      }
      // try next if this was ignorable & didn't have the features
    }
    return false;
  } else if ('ipa' in item) {
    return doItemsMatch(item.ipa, info, item.subset);
  } else if ('raw' in item) {
    return doItemsMatch(item.raw, info, item.subset);
  } else if ('special' in item) {
    const count = checkSpecial(ipa, i, item.special);
    return count === false ? false : makeResult(count, info);
  } else if ('name' in item) {
    const count = checkLookup(item.name, info);
    return count === false ? false : makeResult(count, info);
  }
  // 'empty' always matches 0 characters
  return makeResult(0, info);
}

/** check if everything in items matches ipa[i..], possibly ignoring part of ipa,
 * possibly only needing a subset of /ipa/ to match what's in 'items',
 * returns the number of matching items + the ignored items, or false if it's a mismatch */
function doItemsMatch(items: string[], info: MatchInfo, subset?: boolean): MatchResult | false {
  const { ipa, i, ignore } = info;
  let ignored = 0;
  for (let j = 0; j < items.length;) {
    const source = ipa[i + j + ignored];
    if (subset !== true && source !== items[j]) {
      if (ignore && ignore(source)) {
        ignored++;    // didn't match, but we can ignore it
      } else {
        return false; // mismatch we can't ignore
      }
    } else if (subset === true && !isSubsetMatch(source, items[j])) {
      if (ignore && ignore(source)) {
        ignored++;    // didn't match, but we can ignore it
      } else {
        return false; // mismatch we can't ignore
      }
    } else {
      j++;
    }
  }
  return makeResult(items.length + ignored, info);
}

/** true if every char in toCheck is found in source, though source may have extra chars */
function isSubsetMatch(source: IpaType, toCheck: IpaType): boolean {
  const nSource = source ? source.length : 0;
  const nCheck = toCheck.length;
  if (nSource < nCheck) {
    // can't be a subset match if toCheck is longer
    return false;
  } else if (nSource === nCheck) {
    // must be an exact match if they have the same number of chars
    return source === toCheck;
  }

  // everything in toCheck must be in source
  for (const c of toCheck) {
    if (!source.includes(c)) {
      return false;
    }
  }
  return true;
}

/** check if ipa[i] is a particular kind of special value */
function checkSpecial(ipa: IpaType[], i: number, special: 'word' | 'syllable'): number | false {
  if (special === 'word') {
    return (ipa[i] === ' ' || ipa[i] === undefined) ? 0 : false;
  } else if (special === 'syllable') {
    return ipa[i] === syllableBreak ? 1 : false;
  }
  return false;
}

/** check if ipa[i] is in lookup[name], after possibly ignoring characters */
function checkLookup(name: string, info: MatchInfo): number | false {
  const { ipa, i, ignore, lookup } = info;
  if (!lookup) {
    return false;
  }
  let ignored = 0;
  for (let j = i; j < ipa.length; j++) {
    const source = ipa[j];
    if (ignore && ignore(source)) {
      ignored++;
    } else {
      const possible = lookup[name];
      if (!possible) {
        return false;
      }
      return !possible || !possible.includes(source) ? false : 1 + ignored;
    }
  }
  return false;
}

/** combine new count, old results, & possible new store */
function makeResult(count: number, info: MatchInfo, store?: IpaType[][]): MatchResult {
  const finalStore = store ?? info.store;
  return finalStore ? { count, store: finalStore } : { count };
}
